import { Loader2, type LucideIcon } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import { cn } from '@/lib/utils';

/**
 * Unified Generate CTA used wherever a lesson surface lets the user
 * kick off on-demand content (Video, Podcast, Quiz, Flashcards,
 * Glossary, Highlights). Replaces the bespoke per-renderer CTAs that
 * each had different button styles, gradients, and copy.
 *
 * Visual rules:
 *   - Centered card matching `PanelLoading` proportions so the
 *     transition from idle → loading is just a content swap
 *   - Icon disc on neutral background; per-panel icon carries identity
 *   - Single primary button - black/white from the design system
 */

export interface PanelGenerateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  buttonLabel?: string;
  busy?: boolean;
  onClick: () => void;
  className?: string;
}

export function PanelGenerate({
  icon: Icon,
  title,
  description,
  buttonLabel = 'Generate',
  busy = false,
  onClick,
  className,
}: PanelGenerateProps) {
  return (
    <div
      className={cn(
        'animate-fade-in-up flex h-full w-full flex-col items-center justify-center px-6 py-12 text-center',
        className
      )}
    >
      <div className='flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 dark:bg-muted'>
        <Icon className='h-7 w-7 text-brand-500/80 dark:text-primary' />
      </div>
      <h3 className='mt-4 text-base font-medium text-foreground'>{title}</h3>
      <p className='mt-1 max-w-sm text-sm text-muted-foreground'>
        {description}
      </p>
      <div className='mt-5'>
        <Button onClick={onClick} disabled={busy} className='h-11 gap-2 sm:h-9'>
          {busy && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
          {busy ? 'Starting…' : buttonLabel}
        </Button>
      </div>
    </div>
  );
}
